import React, {useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {IoIosWarning } from 'react-icons/io';
import { useDropzone } from "react-dropzone"
import { onFocusTrigger, onChange, onBlurTrigger, setAndRemoveError, columnAllowsAddNewButton } from './InputFieldEventHandlers';
import {SubmenuContainer, LoadStopsInput } from './loadStopsInput';
import { FaTimesCircle } from "react-icons/fa";
import { BsCalendar3 } from "react-icons/bs";


import { ChargeOrFeeBlock } from './chargeOrFeeBlock';
import { AddButtonWithTitle } from '../../content_management/reusableItems/buttons';
import { SelectionList } from './selectionList';
import { TaskListField } from './taskListField';
import { SelectingMultipleEmployeesForTeams } from '../../../pages/teams/selectingMultipleEmployeesForTeam';
import { Splitter } from './splitter';
import { RateAllocationMatrix } from './rateAllocationMatrix';
import { DriverPayoutEditor } from './driverPayoutEditor';
import { LoadingIcon } from '../../../supplementary_components/loading_icon';
import { DriverWeeklyDeductionsEditor } from '../../content_management/item_page/driver_page_components/driverDeductionsEditor';
import { CustomButtonInForm } from './customButtonInForm';
import { ImageDocumentEditor } from '../../../supplementary_components/imageDocumentEditor/imageDocumentEditor';
import { dateFormatter } from '../../../supplementary_components/supplementaryMethods';
import { CalendarPicker, useCloseOnOutsidePress } from '../../content_management/reusableItems/calendar';
import { SafetyEntriesField } from '../../../pages/inspections/safetyEntriesField';



const rotateBg = keyframes`
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
`;

const inputShellStyles = (props) => css`
    ${props.$position ? props.$position + ';' : ''}
    font-family: var(--main-font-family);
    width: 100%;
    font-size: 1rem;
    font-weight: 600;
    padding: 0.7rem;
    color: var(--dark-gray);
    border: none;
    box-shadow: var(--button-outline);
    -moz-box-shadow: var(--button-outline);
    -webkit-box-shadow: var(--button-outline);
    transition: 0.08s;
    border-radius: 0.4rem;
    &:focus {
        outline: rgba(48, 192, 140, 0.5) solid 0.25rem !important;
    }
    &:disabled {
        opacity: 0.75;
    }
    &:disabled:focus {
        outline: none !important;
    }
`;

export const InputField = styled.span`
    ${(props) => (props.$cusomDisplayMode ? `display:${props.$cusomDisplayMode};` : '')}
    & > input,
    & > select,
    & > textarea {
        ${(props) => inputShellStyles(props)}
    }
    /* Calendar: icon sits inside the field via absolutely positioned sibling; extra right padding only */
    & [data-calendar-input-shell] > input {
        ${(props) => inputShellStyles(props)}
        padding-right: 2.35rem;
    }
`;
const TextField = styled.input`
    background: var(--bg);
    &:disabled {
        /* Reinforce when browser default conflicts with --bg */
        -webkit-text-fill-color: var(--light-gray);
    }
    &:-webkit-autofill,
    &:-webkit-autofill:hover, 
    &:-webkit-autofill:focus, 
    &:-webkit-autofill:active{
        transition: background-color 5000s ease-in-out 0s;
        -webkit-text-fill-color: var(--dark-gray) !important;
    }
    ::placeholder {
        color: var(--light-gray);
        opacity: 0.5; /* Firefox */
    }
    ::-ms-input-placeholder { /* Edge 12 -18 */
        color: var(--light-gray);
        opacity: 0.5; /* Firefox */
    }
    
    
`;

const CalendarFieldShell = styled.div.attrs(() => ({ 'data-calendar-input-shell': true }))`
    position: relative;
    width: 100%;
    display: block;
`;

const CalendarIconButton = styled.button`
    position: absolute;
    right: 0.45rem;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    padding: 0;
    margin: 0;
    border: none;
    border-radius: 0.3rem;
    background: transparent;
    color: var(--main-color);
    cursor: pointer;
    flex-shrink: 0;
    pointer-events: auto;

    &:hover {
        background: var(--extra-light-gray);
    }
`;

const VALID_CALENDAR_TYPES = new Set(['singleDate', 'range', 'both']);

function normalizeCalendarPickType(t) {
    const s = t && String(t).toLowerCase();
    if (s && VALID_CALENDAR_TYPES.has(s)) return s;
    return 'singleDate';
}

function stripCalendarDate(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function formatPickedDateForInput(d) {
    if (!d) return '';
    const t = stripCalendarDate(d);
    const y = t.getFullYear();
    const mo = t.getMonth() + 1;
    const day = t.getDate();
    const intermediate = `${y}-${String(mo).padStart(2, '0')}-${String(day).padStart(2, '0')} 12:00 AM`;
    const { date } = dateFormatter(intermediate);
    if (date) return date;
    return `${String(mo).padStart(2, '0')}/${String(day).padStart(2, '0')}/${String(y).slice(-2)}`;
}

function parseFormattedFragment(fragment) {
    if (!fragment || !String(fragment).trim()) return null;
    const { date, time } = dateFormatter(fragment.trim());
    if (!date) return null;
    const dm = date.match(/^(\d{2})\/(\d{2})\/(\d{2})$/);
    if (!dm) return null;
    const [, mo, d, yy] = dm;
    const year = 2000 + parseInt(yy, 10);
    if (!time) {
        return new Date(year, parseInt(mo, 10) - 1, parseInt(d, 10), 0, 0);
    }
    const tm = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!tm) return null;
    let h = parseInt(tm[1], 10);
    const mi = parseInt(tm[2], 10);
    const ap = tm[3].toUpperCase();
    if (ap === 'PM' && h !== 12) h += 12;
    if (ap === 'AM' && h === 12) h = 0;
    return new Date(year, parseInt(mo, 10) - 1, parseInt(d, 10), h, mi);
}

function parseInputToCalendarDates(raw, selectType) {
    if (!raw || !String(raw).trim()) return { initialStart: null, initialEnd: null };
    const s = String(raw).trim();
    const rangeParts = s.split(/\s*(?:-|–|—)\s*/).filter((p) => p.length > 0);
    const canParseTwo =
        selectType !== 'singleDate' &&
        rangeParts.length >= 2 &&
        parseFormattedFragment(rangeParts[0]) &&
        parseFormattedFragment(rangeParts[1]);
    if (canParseTwo) {
        const a = parseFormattedFragment(rangeParts[0]);
        const b = parseFormattedFragment(rangeParts[1]);
        return {
            initialStart: a ? stripCalendarDate(a) : null,
            initialEnd: b ? stripCalendarDate(b) : null,
        };
    }
    const one = parseFormattedFragment(s);
    return { initialStart: one ? stripCalendarDate(one) : null, initialEnd: null };
}

function TextFieldWithCalendar({ column, suggestionsList, setSuggestionsList, prefill, inputField, abortControllerRef }) {
    const [calendarOpen, setCalendarOpen] = useState(false);
    const calendarShellRef = useRef(null);
    useCloseOnOutsidePress(calendarOpen, calendarShellRef, () => setCalendarOpen(false));
    const selectType = normalizeCalendarPickType(column.calendarPickType);
    const rawValue = () => (inputField.current && inputField.current.value) || '';
    const { initialStart, initialEnd } = parseInputToCalendarDates(rawValue(), selectType);

    const applyCalendarSelection = (...args) => {
        const inp = inputField.current;
        if (!inp) return;
        const a = args[0];
        const b = args[1];
        let nextVal;
        if (b === undefined) {
            nextVal = formatPickedDateForInput(a);
        } else {
            nextVal = `${formatPickedDateForInput(a)} - ${formatPickedDateForInput(b)}`;
        }
        if (column.onblur) {
            nextVal = column.onblur(nextVal, inp);
        }
        inp.value = nextVal;
        onChange({ target: inp }, column, suggestionsList, setSuggestionsList, true);
        setCalendarOpen(false);
    };

    return (
        <CalendarFieldShell ref={calendarShellRef}>
            <TextField
                id={column.id}
                ref={inputField}
                name={column.name}
                disabled={column.disabled}
                type="text"
                $loading={true}
                autoComplete={column.autoComplete ? column.autoComplete : 'new-password'}
                placeholder={column.placeholder}
                onFocus={(e) => {
                    onFocusTrigger(column, e.target, setSuggestionsList, abortControllerRef);
                }}
                onBlur={(e) => {
                    onBlurTrigger(column, e.target, setSuggestionsList, abortControllerRef);
                }}
                onPaste={(e) => {
                    setTimeout(() => {
                        if (e.target.value !== '') {
                            if (column.onblur) {
                                e.target.value = column.onblur(e.target.value);
                            } else {
                                onChange(e, column, suggestionsList, setSuggestionsList, true);
                            }
                        }
                    }, 10);
                }}
                data-value={column.predefinedDatValue || ''}
                defaultValue={column.defaultValue ? column.defaultValue(inputField) : prefill}
                data-old-value={column.saveOldValueInDataAttribute ? column.defaultValue(inputField) : null}
                data-empty-error-applied={false}
                onChange={(e) => {
                    onChange(e, column, suggestionsList, setSuggestionsList);
                }}
                onKeyDown={(e) => {
                    if (e.code === 'Backspace') {
                        setTimeout(() => {
                            if (e.target.value === '') {
                                onChange(e, column, suggestionsList, setSuggestionsList);
                            }
                        }, 50);
                    }
                }}
            />
            <CalendarIconButton
                type="button"
                aria-label="Open calendar"
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                    e.stopPropagation();
                    setCalendarOpen((o) => !o);
                }}
            >
                <BsCalendar3 />
            </CalendarIconButton>
            {calendarOpen ? (
                <CalendarPicker
                    key={`${rawValue()}-${selectType}-${calendarOpen}`}
                    selectType={selectType}
                    popoverAlign="end"
                    initialStart={initialStart || undefined}
                    initialEnd={initialEnd || undefined}
                    onCancel={() => setCalendarOpen(false)}
                    onApply={applyCalendarSelection}
                />
            ) : null}
        </CalendarFieldShell>
    );
}

const TextArea = styled.textarea`
    background: var(--bg);
    &:-webkit-autofill,
    &:-webkit-autofill:hover, 
    &:-webkit-autofill:focus, 
    &:-webkit-autofill:active{
        transition: background-color 5000s ease-in-out 0s;
        -webkit-text-fill-color: var(--dark-gray) !important;
    }
    ::placeholder {
        color: var(--light-gray);
        opacity: 0.5; /* Firefox */
    }
    ::-ms-input-placeholder { /* Edge 12 -18 */
        color: var(--light-gray);
        opacity: 0.5; /* Firefox */
    }
    resize: vertical;
    ::-webkit-resizer {
        background-image: url("data:image/svg+xml,%3Csvg width='64px' height='64px' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg id='SVGRepo_bgCarrier' stroke-width='0'%3E%3C/g%3E%3Cg id='SVGRepo_tracerCarrier' stroke-linecap='round' stroke-linejoin='round'%3E%3C/g%3E%3Cg id='SVGRepo_iconCarrier'%3E%3Cpath d='M21 15L15 21M21 8L8 21' stroke='%23DDDDDD' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3C/path%3E%3C/g%3E%3C/svg%3E");
    background-repeat: no-repeat no-repeat;
    background-position: center center;
    background-size: cover;
      }
`;
export const SelectField = styled.select`
    -webkit-appearance:none;
    -moz-appearance:none;
    appearance: none;
    
    /* Always include background as the last layer */
    background:
        linear-gradient(45deg, transparent 50%, var(--gray) 50%),
        linear-gradient(135deg, var(--gray) 50%, transparent 50%),
        var(--bg); /* Use var(--bg) directly, not a gradient */
    
    background-position:
        calc(100% - 1.4rem) calc(1.05em),
        calc(100% - 0.96rem) calc(1.05em),
        0 0; /* Background covers entire element */
    
    background-size:
        0.5rem 0.4rem,
        0.5rem 0.4rem,
        100% 100%; /* Background covers entire element */
    
    background-repeat: no-repeat;
    padding-right: 2.5em;
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    option{
            background: var(--bg);
    }
`;


const Label = styled.div`
    &[data-loading="true"] {
        &:before {
            content: "";
            position: absolute;
            width: 1.5rem;
            height: 1.5rem;
            right:0.5rem;
            margin-top:2.6rem;
            // top:0.2rem;
            background: url('data:image/svg+xml,<svg stroke="rgba(48, 192, 140, 1)" fill="none" stroke-width="0" viewBox="0 0 26 24" height="1.4em" width="1.4em" xmlns="http://www.w3.org/2000/svg" data-closable="true"><path d="M16 4C11.5817 4 8 7.58172 8 12C8 16.4183 11.5817 20 16 20V17C13.2386 17 11 14.7614 11 12C11 9.23858 13.2386 7 16 7V4Z" fill="rgba(48, 192, 140, 1)"></path></svg>') no-repeat right center;
            // background-color:black;
            
            ${({ animate }) => css`
                animation: ${rotateBg} 1s linear infinite;
            `}
        }
    }
    font-size: 1.1rem;
    font-weight:500;
    padding: 0.8rem 0;
    ${props => !props.firstLabel ? 'margin-top:1rem' : ''};
    color:var(--dark-gray);
    ${props => props.mandatory ? `&:after {
        content: " *";
        font-weight:900;
        color:var(--alert-color);
    }` : ''};
`;
const ErrorMessage = styled.div`
    margin-top:0.5rem;
    font-weight:500;
    color: var(--alert-color);
    display:none;
    align-items:center;
`;
export const OptionsContainer = styled.div`
    
    position:relative;
    width:calc(100% + 0.1rem);
    overflow: visible; 
    z-index:100;
    
    &>div:not(#loadingIcon){
        left:-0.05rem;
        position: absolute;
        background-color:var(--bg);
        margin-top:0.2rem;
        width:inherit;
        border:none;
        box-shadow: var(--button-outline);
        border-radius:.5rem;
        max-height:17rem;
        overflow-x:hidden;
        overflow-y: auto;
        &>*:not(#loadingIcon){
            cursor:pointer;
            padding: 0.7rem 1rem;
            // font-size:1.1rem;
            :hover{
                background-color:var(--extra-light-gray);
                
            }
        }
    }
    &>div>#loadingIcon{
        position: absolute;
        top:0;
        width:inherit;
        height:100%;
    }
`;

const DropBox = styled.div`
    display:flex;
    width:100%;
    height:8rem;
    border:none;
    box-shadow: var(--button-outline);
    -moz-box-shadow: var(--button-outline);
    -webkit-box-shadow: var(--button-outline);
    transition:0.08s;
    border-radius:0.4rem;
    justify-content:center;
    align-items:center;
    color:var(--light-gray);
    cursor:pointer;
    &[data-being-dragged="true"] {
        outline  : rgba(48, 192, 140, 0.5) solid 0.25rem !important;
    }
    p{
        text-align: center;
        line-height: 1.4rem;
        padding-top:1.5rem;
        .maxFileSize{
            font-size:0.8rem;
            color:var(--lighter-gray);
        }
    }
    
`
const LoadedFile = styled.div`
    display:flex;
    align-items:center;
    color:var(--dark-gray);
    svg{
        color:var(--alert-color);
        cursor:pointer;
    }
`
const SelectedFiles = styled.div`
    display:flex;
    flex-direction:column;
    gap:0.4rem;
    width:100%;
    padding-bottom:0.75rem;
    margin-bottom:0.25rem;
`
const DropBoxStack = styled.div`
    display:flex;
    flex-direction:column;
    width:100%;
    gap:0.5rem;
    padding-bottom:0.5rem;
`
const SubMenu = styled.div`
    width:100%;
    .label{
        padding-top:0.5rem;
        margin-right:0.5rem;
        float:right;
        cursor:pointer;
        color: var(--main-color);
        border-bottom: 1px dashed var(--main-color);
    }
`;
export const SuggestedOption = styled.div`
    color:var(--dark-gray);
`
export const OptionSubPart = styled.span`
    color:var(--light-gray);
`
const PlainSpan = styled.span`
    & [data-calendar-input-shell] > input {
        padding-right: 2.35rem;
    }
`;
export const InputFieldDelegate = ({index, column, prefill, noStyling, closePopup})=>{
    
    const [suggestionsList, setSuggestionsList] = useState([]);
    if(!column)return null;
    if(column.hiddenField)return <input hidden={true} name={column.name} data-value={JSON.stringify(column.defaultValue())} defaultValue={column.defaultValue()} />
    let Wrapper = noStyling ? PlainSpan : InputField;
    return <>
        <Wrapper className="InputFieldWrapper" $cusomDisplayMode={column.customDisplayMode} >
            {column.label?<Label id={column.name+'Label'} data-loading="false"  firstLabel={index===0}  mandatory={column.mandatory}>{column.label}</Label>:''}
            
            {InputByType(column, suggestionsList, setSuggestionsList, prefill, closePopup)}
            
            {column.suggestionsEnabled ? <OptionsContainer id={column.name+'Options'} className={'dropDownSuggestionMenu'} style={{display:'none'}} >
                <div>
                    {suggestionsList.map((option, index)=>{
                        if(column.exceptionVal){
                            const tagData = document.querySelector(column.exceptionVal.inputName).getAttribute('data-value');
                            if (tagData !== 'null' &&  tagData !== 'undefined' && tagData !== ''){                                
                                const val = JSON.parse(tagData);
                                if(val !== null & val[column.exceptionVal.fieldName] === option[column.exceptionVal.fieldName]){
                                    return null;
                                }
                            }
                        }
                        if(option.loading)return null;
                        let mainPart, subPart;  
                        
                        if(column.twoPartValue){
                            mainPart = column.twoPartValue.mainPart(option)
                            subPart = column.twoPartValue.subPart(option)
                        }        
                        const displayVal = column.displayValueGenerator(option);
                        return <SuggestedOption key={index} style={{cursor:option[column.availableOptionDefiningColumnName] === false ?'not-allowed':''}} data-raw-val={JSON.stringify(option)}>{column.twoPartValue ? mainPart : displayVal} <OptionSubPart>{column.twoPartValue ? `${subPart}` : ''}</OptionSubPart>
                        </SuggestedOption>
                    })}
                    
                    {columnAllowsAddNewButton(column) ? AddButtonWithTitle({title:column.addNewButton.label})  : null}
                    {suggestionsList.length>0 && suggestionsList[suggestionsList.length-1].loading ?
                    
                    
                        <div id="loadingIcon"> <LoadingIcon state={true} height="100%" wrapperHeight="100%" iconHeight={"2rem"} />  </div>
                    : null}
                </div>
            </OptionsContainer> : null}
            {column.mandatory || column.hasConstraints ? <ErrorMessage id={column.name+'Error'}><IoIosWarning />&nbsp;<span id={'errorMessage'}></span></ErrorMessage> : null}
        </Wrapper>
        
        {column.subMenu && !column.ignoredSubMenu ? <SubMenu> 
            <div className='label' style={{display:column.subMenu.openByDefault?'none':"block"}}  onClick={(e)=>{
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
            }}>{column.subMenu.title}</div> 
            <SubmenuContainer $topMargin={'0.5rem'} style={{display:column.subMenu.openByDefault?"block":'none'}} $gridTemplateArea={column.subMenu.areaTemplate} $gridTemplateColumns={column.subMenu.columnTemplate} >
                <div>
                    <span >
                        {column.subMenu.columns.map((subMenuField, index)=>{
                            return <InputFieldDelegate 
                                index = {index}
                                key={index}
                                column={subMenuField}
                            />
                            // return <input type='text' name={subMenuField.name} key={subMenuField.name} placeholder={subMenuField.label} autoComplete="new-password" />
                        })}
                    </span>
                </div>
            </SubmenuContainer>
        </SubMenu> : null}
    </>;
}
const FieldRowLayout = styled.span`
    display: grid;
    width: 100%;
    gap: 0.5rem;
    grid-template-columns: repeat(var(--field-count, 1), minmax(0, 1fr));
`;

function FieldRow({ column, closePopup }) {
    const fields = column.fields || [];
    return (
        <FieldRowLayout style={{ '--field-count': fields.length || 1 }}>
            {fields.map((field, index) => (
                <InputFieldDelegate
                    key={field.name || index}
                    index={0}
                    column={field}
                    closePopup={closePopup}
                />
            ))}
        </FieldRowLayout>
    );
}

const InputByType = (column, suggestionsList, setSuggestionsList, prefill, closePopup)=>{
    
    
    const inputField = useRef(null);
    const abortControllerRef = useRef(null); // Add this ref
    switch (column.type) {
        case 'customButton':
            return <CustomButtonInForm column={column} postSuccessAction={closePopup} />;
        case 'dropBox':
            return <FileDropBox column={column} defaultFile={column.defaultValue ? column.defaultValue() : null} inputField={inputField} />;
        case 'select':
            return <SelectField 
                ref={inputField}
                name={column.name} 
                defaultValue = {column.defaultValue ? column.defaultValue(inputField) : null} 
                disabled={column.disableVal && column.val === column.disableVal}
                onChange={(event) => {
                    if (column.onChange) column.onChange(event);
                    if (event.target.value !== '' && event.target.getAttribute('data-empty-error-applied') === 'true') {
                        setAndRemoveError(column, event.target, 'This field cannot be empty', false);
                    }
                }}
            > 
                {column.dataset && (typeof column.dataset === 'function' ? column.dataset() : column.dataset).map((option, index)=>{
                    const isObjectOption = typeof option === 'object' && option !== null;
                    const optionValue = isObjectOption
                        ? (typeof column.selectOptionValue === 'function'
                            ? column.selectOptionValue(option)
                            : (option.value ?? option.builtin ?? option.name ?? ''))
                        : option;
                    const optionLabel = isObjectOption
                        ? (option.label ?? option.name ?? String(optionValue))
                        : option;
                    const optionKey = isObjectOption ? (option.id ?? `${option.builtin ?? optionValue}-${index}`) : index;
                    const disabledFromCustomVals = Array.isArray(column.customDisabledVals) && (
                        column.customDisabledVals.includes(option)
                        || column.customDisabledVals.includes(optionValue)
                        || (isObjectOption && option.builtin != null && column.customDisabledVals.includes(option.builtin))
                    );
                    const disabledFromDisableVal = column.disableVal && (
                        option === column.disableVal
                        || optionValue === column.disableVal
                        || (typeof column.disableVal === 'function' && (
                            option === column.disableVal()
                            || optionValue === column.disableVal()
                        ))
                    );
                    const disabled = !!(disabledFromCustomVals || disabledFromDisableVal);
                    return (
                        <option
                            key={optionKey}
                            value={optionValue}
                            disabled={disabled}
                        >
                            {optionLabel}
                        </option>
                    );
                })}
            </ SelectField>
        case "employeeSelectionForGeneratingTeams":
            return <SelectingMultipleEmployeesForTeams defaultData={column.defaultValue} nameFormatter={column.suggestionOptionsFormatter} currentTeamId={column.currentTeamId} />
        case "taskList":
            return <TaskListField setDataMethod={column.setDataMethod} defaultVal={column.defaultValue} changedTasks={column.changedTasks} newTask={column.newTask} taskWithDuration={column.taskWithDuration} />
        case "selectionList":
            return <SelectionList data={column} />
        case "fieldRow":
            return <FieldRow column={column} closePopup={closePopup} />
        case 'chargeOrFeeBlock':
            return <ChargeOrFeeBlock  data={column.defaultValue()}  type={column.name} customProps={column.customProps} />;
        case "driverWeeklyDeductionsEditor":
            return <DriverWeeklyDeductionsEditor  data={column.parsedData}  type={column.name} structureData={column.structureData} gridTemplateColumns={column.gridTemplateColumns} titleGridTemplateColumns={column.titleGridTemplateColumns} />;
        case 'loadPickupDeliveryStops':
            return <LoadStopsInput column={column} editMode={column.editMode}></LoadStopsInput>;
        case "rateDistribution":
            return <Splitter viewMode={false} data={column.defaultValue} amountForInvoice={column.amountForInvoice} splitBy={column.splitBy} />
        case 'rateAllocationMatrix':
            return (
                <RateAllocationMatrix
                    viewMode={false}
                    rateAllocationMatrix={column.defaultValue?.rateAllocationMatrix}
                    loadExecutors={column.defaultValue?.loadExecutors}
                    rateDistributionByWeeks={column.defaultValue?.rateDistributionByWeeks}
                    amountForInvoice={column.amountForInvoice}
                />
            );
        case 'driverPayoutEditor':
            return (
                <DriverPayoutEditor
                    data={column.defaultValue?.data}
                    driverPayout={column.defaultValue?.driverPayout}
                    trucks={column.defaultValue?.trucks}
                />
            );
        case 'textArea':
            return <TextArea
                ref={inputField}
                name={column.name} 
                type="text"
                rows={column.rowCount}
                autoComplete={column.autoComplete ? column.autoComplete : "new-password"}
                defaultValue={column.defaultValue ? column.defaultValue() : null}
                onChange={(event) => {
                    if (event.target.value !== '' && event.target.getAttribute('data-empty-error-applied') === 'true') {
                        setAndRemoveError(column, event.target, 'This field cannot be empty', false);
                    }
                }}
            ></TextArea>
        case 'safetyEntries':
            return <SafetyEntriesField column={column} />;
        default:
            if (column.calendarPickType) {
                return (
                    <TextFieldWithCalendar
                        column={column}
                        suggestionsList={suggestionsList}
                        setSuggestionsList={setSuggestionsList}
                        prefill={prefill}
                        inputField={inputField}
                        abortControllerRef={abortControllerRef}
                    />
                );
            }
            return <TextField 
            id={column.id}
            ref={inputField}
            name={column.name} 
            disabled={column.disabled}
            type="text"
            $loading={true}
            autoComplete={column.disableBrowserSuggestions ? 'one-time-code' : (column.autoComplete ? column.autoComplete : "new-password")}
            readOnly={column.disableBrowserSuggestions || undefined}
            onMouseDown={(event) => {
                if (column.disableBrowserSuggestions) event.target.readOnly = false;
            }}
            placeholder ={column.placeholder}
            onFocus={(e)=>{
                if (column.disableBrowserSuggestions) e.target.readOnly = false;
                onFocusTrigger(column, e.target, setSuggestionsList, abortControllerRef);
            }}
            onBlur={(e)=>{ 
                onBlurTrigger(column, e.target, setSuggestionsList, abortControllerRef);
            }}
            onPaste={(e)=>{
                setTimeout(() => {
                    if(e.target.value !== ''){
                        if (column.onblur){
                            e.target.value = column.onblur(e.target.value)
                        }else{
                            onChange(e, column, suggestionsList, setSuggestionsList, true, abortControllerRef);
                        }
                    }
                }, 10)
            }}
            data-value={column.predefinedDatValue || ''}
            defaultValue = {column.defaultValue ? column.defaultValue(inputField) : prefill} 
            data-old-value={column.saveOldValueInDataAttribute ? (column.defaultValue(inputField))  : null}
            data-empty-error-applied = {false}
            
            onChange={(e)=>{
                onChange(e, column, suggestionsList, setSuggestionsList, false, abortControllerRef);
            }}
            onKeyDown={(e)=>{
                if(e.code === "Backspace"){
                    setTimeout(() => {
                        if(e.target.value === ''){
                            onChange(e, column, suggestionsList, setSuggestionsList, false, abortControllerRef);
                        }
                    }, 50);
                }
            }}
        />
    }
}

const getClipboardFiles = (event) => {
    const fromFiles = event.clipboardData?.files;
    if (fromFiles?.length) {
        return Array.from(fromFiles);
    }
    const items = event.clipboardData?.items;
    if (!items) {
        return [];
    }
    const files = [];
    for (const item of items) {
        if (item.kind === 'file') {
            const file = item.getAsFile();
            if (file) {
                files.push(file);
            }
        }
    }
    return files;
};

const isEditablePasteTarget = (target) => {
    if (!target || target === document.body) {
        return false;
    }
    const tag = target.tagName;
    if (tag === 'TEXTAREA' || tag === 'SELECT') {
        return true;
    }
    if (tag === 'INPUT' && target.type !== 'file') {
        return true;
    }
    return Boolean(target.isContentEditable);
};

const isImageFile = (candidate) => {
    const name = (candidate?.name || '').toLowerCase();
    const type = (candidate?.type || '').toLowerCase();
    return (
        type === 'image/jpeg' ||
        type === 'image/jpg' ||
        type === 'image/png' ||
        name.endsWith('.jpg') ||
        name.endsWith('.jpeg') ||
        name.endsWith('.png')
    );
};

const isAcceptedDropFile = (candidate, allowsImages) => {
    const name = (candidate.name || '').toLowerCase();
    const type = (candidate.type || '').toLowerCase();
    if (allowsImages) {
        return type === 'application/pdf' || name.endsWith('.pdf') || isImageFile(candidate);
    }
    return type === 'application/pdf' || name.endsWith('.pdf');
};

/** Empty dropzones register here so Cmd/Ctrl+V can target one without hover. */
const emptyFileDropBoxes = [];

const resolvePasteDropBox = (eventTarget) => {
    const available = emptyFileDropBoxes.filter(({ el }) => el.current);
    if (!available.length) {
        return null;
    }
    const focused = available.find(({ el }) => {
        const node = el.current;
        return node === document.activeElement || node.contains(document.activeElement);
    });
    if (focused) {
        return focused;
    }
    const hovered = available.find(({ el }) => el.current.matches(':hover'));
    if (hovered) {
        return hovered;
    }
    const containing = available.find(({ el }) => el.current.contains(eventTarget));
    if (containing) {
        return containing;
    }
    // Tab focused / no hover: use the first empty dropzone (usually the primary upload field).
    return available[0];
};

const fileIdentity = (candidate) =>
    `${candidate?.name || ''}|${candidate?.size || 0}|${candidate?.lastModified || 0}`;

/** Selected files for a dropBox column (multi or single). */
export const getDropBoxFiles = (column) => {
    if (column?.multiple && Array.isArray(column.files) && column.files.length) {
        return column.files;
    }
    return column?.file ? [column.file] : [];
};

const FileDropBox = ({column, defaultFile})=>{
    const allowsMultiple = Boolean(column.multiple);
    const [file, setFile] = useState(defaultFile);
    const [files, setFiles] = useState(() => {
        if (allowsMultiple) {
            if (Array.isArray(column.files) && column.files.length) {
                return column.files;
            }
            if (defaultFile) {
                return [defaultFile];
            }
            return [];
        }
        return [];
    });

    if (!allowsMultiple && (file === null || column.file === null) && defaultFile) {
        column.file = defaultFile;
    }
    if (allowsMultiple) {
        column.files = files;
        column.file = files[0] || null;
    }

    const [editorQueue, setEditorQueue] = useState([]);
    const myRef = useRef(null);
    const dropBoxRef = useRef(null);
    const fileRef = useRef(file);
    const filesRef = useRef(files);
    fileRef.current = file;
    filesRef.current = files;
    const allowsAny = column.fileType === "any";
    // Opt-in: images are routed through the crop/deskew editor before attaching.
    const usesImageEditor = Boolean(column.imageEditor);
    const allowsImages = allowsAny || usesImageEditor;
    const rejectHint = allowsImages
        ? "<span style='color:var(--alert-color)'>Wrong file format.</span> <br />Accepted: PDF, JPG, PNG<br /> <u>Select</u>, Drop, or Paste file here"
        : "<span style='color:var(--alert-color)'>Wrong file format.</span> <br />File needs to be in PDF<br /> <u>Select</u>, Drop, or Paste file here";

    const clearFieldError = useCallback(() => {
        if (myRef.current?.parentElement?.nextSibling != null) {
            if (myRef.current.parentElement.nextSibling.id === `${column.name}Error`) {
                myRef.current.parentElement.nextSibling.style.display = 'none';
            }
        }
    }, [column.name]);

    const showRejected = useCallback(() => {
        if (!myRef.current) {
            return;
        }
        myRef.current.parentElement.setAttribute('data-being-dragged', 'false');
        myRef.current.innerHTML = rejectHint;
    }, [rejectHint]);

    const syncSingleFile = useCallback((acceptedFile) => {
        column.file = acceptedFile;
        setFile(acceptedFile);
        if (column.onceFileLoaded) {
            column.onceFileLoaded();
        }
    }, [column]);

    const syncMultipleFiles = useCallback((nextFiles) => {
        column.files = nextFiles;
        column.file = nextFiles[0] || null;
        setFiles(nextFiles);
        if (nextFiles.length > 0 && column.onceFileLoaded) {
            column.onceFileLoaded();
        }
    }, [column]);

    const processIncomingFiles = useCallback((incomingFiles) => {
        if (!incomingFiles?.length) {
            return false;
        }
        if (!allowsMultiple && fileRef.current !== null) {
            return false;
        }

        const accepted = [];
        let rejected = false;
        for (const candidate of incomingFiles) {
            if (isAcceptedDropFile(candidate, allowsImages)) {
                accepted.push(candidate);
            } else {
                rejected = true;
            }
        }
        if (!accepted.length) {
            showRejected();
            return true;
        }
        if (rejected) {
            showRejected();
        }

        clearFieldError();

        // With the image editor enabled, images detour through the crop/deskew
        // tool; only PDFs attach directly.
        let directFiles = accepted;
        if (usesImageEditor) {
            const images = accepted.filter(isImageFile);
            directFiles = accepted.filter((candidate) => !isImageFile(candidate));
            if (images.length) {
                const toQueue = allowsMultiple ? images : images.slice(0, 1);
                setEditorQueue((prev) => [...prev, ...toQueue]);
            }
        }

        if (allowsMultiple) {
            if (directFiles.length) {
                const existingKeys = new Set(filesRef.current.map(fileIdentity));
                const merged = [...filesRef.current];
                for (const candidate of directFiles) {
                    const key = fileIdentity(candidate);
                    if (!existingKeys.has(key)) {
                        existingKeys.add(key);
                        merged.push(candidate);
                    }
                }
                syncMultipleFiles(merged);
            }
            return true;
        }

        if (directFiles.length) {
            syncSingleFile(directFiles[0]);
        }
        return true;
    }, [allowsImages, allowsMultiple, clearFieldError, showRejected, syncMultipleFiles, syncSingleFile, usesImageEditor]);

    const handlePaste = useCallback((event) => {
        if (!allowsMultiple && fileRef.current !== null) {
            return;
        }
        const clipboardFiles = getClipboardFiles(event);
        if (!clipboardFiles.length) {
            return;
        }
        if (isEditablePasteTarget(event.target) && !dropBoxRef.current?.contains(event.target)) {
            return;
        }
        const targetEntry = resolvePasteDropBox(event.target);
        if (!targetEntry || targetEntry.el.current !== dropBoxRef.current) {
            return;
        }
        if (processIncomingFiles(clipboardFiles)) {
            event.preventDefault();
            event.stopPropagation();
        }
    }, [allowsMultiple, processIncomingFiles]);

    useEffect(() => {
        // Single-file mode only accepts paste while empty; multi mode always can add more.
        if (!allowsMultiple && file !== null) {
            return undefined;
        }
        const entry = { el: dropBoxRef };
        emptyFileDropBoxes.push(entry);
        document.addEventListener('paste', handlePaste);
        return () => {
            const index = emptyFileDropBoxes.indexOf(entry);
            if (index !== -1) {
                emptyFileDropBoxes.splice(index, 1);
            }
            document.removeEventListener('paste', handlePaste);
        };
    }, [allowsMultiple, file, handlePaste]);

    const handleEditorComplete = useCallback((processedFile) => {
        setEditorQueue((prev) => prev.slice(1));
        clearFieldError();
        if (allowsMultiple) {
            syncMultipleFiles([...filesRef.current, processedFile]);
        } else if (fileRef.current === null) {
            syncSingleFile(processedFile);
        }
    }, [allowsMultiple, clearFieldError, syncMultipleFiles, syncSingleFile]);

    const handleEditorCancel = useCallback(() => {
        setEditorQueue((prev) => prev.slice(1));
    }, []);

    const editorOverlay = usesImageEditor && editorQueue.length > 0 ? (
        <ImageDocumentEditor
            file={editorQueue[0]}
            onComplete={handleEditorComplete}
            onCancel={handleEditorCancel}
        />
    ) : null;

    const { getRootProps, getInputProps } = useDropzone({
        accept: allowsImages
            ? {
                'application/pdf': ['.pdf'],
                'image/jpeg': ['.jpg', '.jpeg'],
                'image/png': ['.png'],
            }
            : {
                'application/pdf': ['.pdf'],
            },
        multiple: allowsMultiple,
        onDrop: (acceptedFiles) => {
            processIncomingFiles(acceptedFiles);
        },
        onDropRejected:()=>{
            showRejected();
        },
        onDragEnter: () =>{
            myRef.current?.parentElement?.setAttribute('data-being-dragged', 'true');
        },
        onDragLeave:() =>{
            myRef.current?.parentElement?.setAttribute('data-being-dragged', 'false');
        }
    })

    const clearSingleFile = () => {
        try {
            const rateConfLabel = document.querySelector('div[id="rateConLabel"]');
            if (rateConfLabel) {
                setAndRemoveError({name:"rateCon"}, rateConfLabel, null, false);
            }
        } catch (error) {}
        setFile(null);
        column.file = null;
    };

    const removeMultipleFileAt = (index) => {
        const next = filesRef.current.filter((_, i) => i !== index);
        syncMultipleFiles(next);
    };

    if (!allowsMultiple && file !== null) {
        return <>
            <LoadedFile>
                {file.name || file.fileName} &nbsp; <FaTimesCircle onClick={clearSingleFile} />
            </LoadedFile>
            {editorOverlay}
        </>
    }

    const { ref: dropzoneRef, ...rootProps } = getRootProps({
        tabIndex: 0,
        onPaste: handlePaste,
    });
    const dropZone = (
        <DropBox
            data-being-dragged={"false"}
            {...rootProps}
            ref={(node) => {
                dropBoxRef.current = node;
                if (typeof dropzoneRef === 'function') {
                    dropzoneRef(node);
                } else if (dropzoneRef) {
                    dropzoneRef.current = node;
                }
            }}
        >
            <input {...getInputProps()} name={column.name} />
            <p ref={myRef}>
                <u>Select</u>, Drop, or Paste {allowsMultiple ? 'files' : 'file'} here
                <br />
                <span className="maxFileSize">
                    {usesImageEditor
                        ? 'PDF or JPG/PNG · Max File Size: 30 MB'
                        : allowsAny ? 'Max File Size: 30 MB' : 'PDF only · Max File Size: 30 MB'}
                </span>
            </p>
        </DropBox>
    );

    if (!allowsMultiple) {
        return <>
            {dropZone}
            {editorOverlay}
        </>;
    }

    return (
        <DropBoxStack>
            {files.length > 0 ? (
                <SelectedFiles>
                    {files.map((selected, index) => (
                        <LoadedFile key={`${fileIdentity(selected)}-${index}`}>
                            {selected.name || selected.fileName} &nbsp;{' '}
                            <FaTimesCircle onClick={() => removeMultipleFileAt(index)} />
                        </LoadedFile>
                    ))}
                </SelectedFiles>
            ) : null}
            {dropZone}
            {editorOverlay}
        </DropBoxStack>
    );
}

